import { Component, Input, ViewChild, SecurityContext, ElementRef, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { HelperService } from 'app/services/helper.service';
import { CrSyncService, CrAlert, CrAlertState, CrRedirectRoute } from '../../../services/crsync.service';
import { environment } from 'environments/environment';
import { AlarmService } from 'app/services/alarm.service';
import { Observable, noop } from 'rxjs';
import { IAlarmReasonsRequest } from 'app/models/alarm-reasons.model';
import { ApiService } from 'app/services/api.service';
import { MarkAlarmReasonComponent } from './mark-alarm-reason/mark-alarm-reason.component';
import { SafePipe, TelephonifyPipe, NewLineToBreakPipe, PickUpActivationName, CalculateAlertTime, AlreadyAdded } from '../../../pipes/pipes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrNotesFormComponent } from '../control-room-notes/cr-notes-form/cr-notes-form.component';
import { CrNotesListComponent } from '../control-room-notes/cr-notes-list/cr-notes-list.component';
import { AddNoteComponent } from './add-note/add-note.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AsyncPipe, CommonModule } from '@angular/common';
import { CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ControlRoomService } from 'app/services/control-room.service';
import { NotificationBadgeComponent } from 'app/components/notifications/notification-badge/notification-badge.component';
import { DomSanitizer } from '@angular/platform-browser';
import { DeviceClass } from 'app/models/device.model';
import moment from 'moment';
import { AlarmHistoryComponent } from '../alarm-history/alarm-history.component';
import { CustomerRulesComponent } from '../customer-rules/customer-rules.component';
import { PhonebookButtonComponent } from '../phonebook/phonebook-button/phonebook-button.component';
import { MatDialog } from '@angular/material/dialog';
import { CompleteAlertConfirmDialogComponent } from '../complete-alert-confirm-dialog/complete-alert-confirm-dialog.component';
import { SendToAppConfirmDialogComponent } from '../send-to-app-confirm-dialog/send-to-app-confirm-dialog.component';

interface PostedMessage {
  url: string;
  cameraId: string;
  cameraGroupId: string;
  visitId: string;
  status: string;
  deltaY?: number;
  canAnonymize?: boolean;
  isAnonymized: boolean;
  content?: { width: number; height: number }; //wrapper is 360px wide, 690px tall (video 622 + buttons ~58 + padding 10)
  video?: { width: number; height: number, top: number, left: number };// rendered element at 350x622 (350 = 360 - 10px padding), positioned at top:5 left:5
  videoNative?: { width: number; height: number }; //  the camera's actual stream resolution
}
@Component({
  selector: 'control-room-alert-panel-started',
  templateUrl: './control-room-alert-panel-started.component.html',
  styleUrls: ['../control-room.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    // modules
    CommonModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatTooltipModule,
    TranslateModule,
    // pipes
    AsyncPipe,
    SafePipe,
    TelephonifyPipe,
    NewLineToBreakPipe,
    PickUpActivationName,
    CalculateAlertTime,
    AlreadyAdded,
    // components
    CrNotesListComponent,
    AddNoteComponent,
    CrNotesFormComponent,
    MarkAlarmReasonComponent,
    NotificationBadgeComponent,
    AlarmHistoryComponent,
    CustomerRulesComponent,
    PhonebookButtonComponent
  ]
})
export class ControlRoomAlertPanelStartedComponent implements OnInit, OnDestroy {

  constructor(
    public helper: HelperService,
    private crSync: CrSyncService,
    public crService: ControlRoomService,
    private alarmService: AlarmService,
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) { }

  alertIsEverwatch = (alert: CrAlert) => alert.deviceType === DeviceClass.Everwatch;

  // @ViewChild(AlarmReasons) checkboxForm: AlarmReasons;

  checkboxListIsOpen: boolean = false;
  showFurtherActions$: Observable<boolean>;

  get alerts(): CrAlert[] {
    return this.crService.startedAlerts
  }
  get visits() {
    return this.crService.startedVisits
  }
  get teamsAlerts() {
    return this.crService.teamsStartedAlerts
  }

  @Input() showTeamAlerts: boolean | undefined = false;
  addedCameraVisitNotes: string[] = [];
  videoControllerBaseUrl: string = environment.videoServerBaseUrl;

  // Store iframe heights per camera with metadata
  iframeHeightsMap: Map<string, { height: number | null | undefined; canAnonymize?: boolean }> = new Map();

  private messageHandler: (event: MessageEvent) => void;
  private DEFAULT_HEIGHT = '19vw'; // default height when contentHeight is not available in not-Nightowl visits

  /**
   * To change the default height, modify the DEFAULT_HEIGHT property.
   * @param px
   * @returns The height in vw units as a number.
   */
  private pxToVw(px: number): number {
    return (px / window.innerWidth) * 100;
  }

  /**
   * Returns the height of the iframe in vw units for CSS usage.
   * @param cameraId 
   * @returns The height of the iframe in vw units as a string, e.g., "XX.Xvw" or "0vw" if not available.
   */
  getHeightInVw(cameraId: string): string {
    const entry = this.iframeHeightsMap.get(cameraId);
    if (!entry?.height) return this.DEFAULT_HEIGHT;
    return `${this.pxToVw(entry.height + 12).toFixed(2)}vw`;
  }

  // needed to use the enum CrAlertState in HTML template
  emaAlertState = CrAlertState;
  showNoteForm = false;
  @ViewChild(MarkAlarmReasonComponent) MarkAlarmReasonComponent: MarkAlarmReasonComponent;
  fireAlreadyAddedPipe = this.alarmService.checkedTasksLength$
  language: string | null = null;
  features: string | null = null;

  noteText: string = "";
  iFrameClicked: string | null = null;

  ngOnInit() {
    this.language = localStorage.getItem('language');
    this.messageHandler = (event: MessageEvent) => {
      // Only handle messages from any of this component's iframes
      const iframes: NodeListOf<HTMLIFrameElement> = this.elementRef.nativeElement.querySelectorAll('iframe');
      let isValidSource = false;
      for (const iframe of iframes) {
        if (iframe.contentWindow === event.source) {
          isValidSource = true;
          break;
        }
      }
      if (!isValidSource) {
        return;
      }

      if (event.data?.status === 'wheel' && typeof event.data?.deltaY === 'number') {
        window.scrollBy({ top: event.data.deltaY, behavior: 'auto' });
        return;
      }

      if (event.data?.status === 'loaded' || event.data?.status === 'loadingError') {
        this.isLoaded(event);
      }

      if (event.data?.status === 'videoPageReady' && event.data?.cameraId) {
        this.iframeHeightsMap.set(event.data?.cameraId, {
          height: null,
          canAnonymize: event.data?.canAnonymize
        });
      }

      if (event.data?.status === 'contentResize') {
        const message: PostedMessage = event.data;
        const contentHeight = message.content?.height;
        const cameraId = message.cameraId;

        if (cameraId) {
          const existing = this.iframeHeightsMap.get(cameraId);
          this.iframeHeightsMap.set(cameraId, {
            height: contentHeight,
            canAnonymize: existing?.canAnonymize
          });
        }
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  ngOnDestroy(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
  }

  trackByDeviceId(_: number, device: any) {
    return device.deviceId;
  }

  handleNoteText(text: string, alertId: string) {
    const ix = this.crService.startedAlerts.findIndex((a) => a.alertId === alertId);
    this.crService.startedAlerts[ix].noteCache = text;
  }

  sanitizeUrl(url: string) {
    return this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, url);
  }

  getTeamMemberName(alert: CrAlert): string {
    const member = this.crSync.getTeamMember(alert.startedBy, alert.userId);
    if (member) return member.name || "";
    return "";
  }

  rewriteLanguage(UILanguage: string): string {
    let LangId: string;
    switch (UILanguage) {
      case 'en':
        LangId = 'EN'
        break;
      case 'fi':
        LangId = 'FI'
        break;
      case 'sv':
        LangId = 'SE'
        break;
      case 'ee':
        LangId = 'EE'
        break;
      default:
        LangId = 'EN'
    }
    return LangId
  }

  /**
   * Creates an alarm reasons request object for the API.
   *
   * @param alertId {string}  CrAlert objects alertId.
   * @returns {IAlarmReasonsRequest} - The request object for API.
   */
  createAlarmReasonsRequestObject(alertId: string) {
    const requestObject: IAlarmReasonsRequest = {
      taskId: alertId,
      langId: this.rewriteLanguage(this.language || 'en'),
      alarmReasons: this.alarmService.getAlarmReasonsForAlert(alertId)
    }
    return requestObject
  }

  calculateRemainingTime(data: any) {
    if (!this.crService.alertIsNightowlVisit(data)) {
      const timeRemaining = moment(data.end).diff(moment(), 'seconds');
      return timeRemaining;
    };

    const endTime = moment(data.cameraVisitStartTime).add(data.cameraVisitDurationMinutes, 'minutes');
    const timeLeft = endTime.diff(new Date(), 'seconds');
    return timeLeft;
  }

  /**
   * Asks the operator to confirm before the alert is completed. Finishing an
   * alert cannot be undone, so the alert is only completed on an explicit
   * confirmation - cancelling or dismissing the dialog leaves it untouched.
   *
   * @param {CrAlert} alert - The alert object the operator wants to complete.
   */
  confirmCompleteAlert(alert: CrAlert) {
    const dialogRef = this.dialog.open(CompleteAlertConfirmDialogComponent, {
      width: '600px',
      data: { header: alert.header, productId: alert.productId }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.completeAlert(alert)
      }
    })
  }

  /**
   * Completes an alert by
   * sending an alarm reasons request to the API,
   * removing alarm reasons from storage,
   * and after successful call to API by performing additional actions in control room service
   *
   * @param {CrAlert} alert - The alert object to be completed.
   */
  completeAlert(alert: CrAlert) {
    const requestObject = this.createAlarmReasonsRequestObject(alert.alertId)

    this.alarmService.removeAlarmReasonsFromStorage(alert.alertId)
    this.iFrameClicked = null;

    this.api.setReasonsToAlert(requestObject).subscribe(
      noop,
      noop,
      () => {
        this.crSync.completeAlert(alert.userId, alert.alertId)
      }
    )
  }

  /**
   * Sends alarm reasons to the application,
   * removes alarm reasons from storage
   * and after successful call to API performs additional actions in control room service
   *
   * @param {CrAlert} alert - The alert object to be sent to the application.
   */
  sendToApp(alert: CrAlert) {

    const requestObject = this.createAlarmReasonsRequestObject(alert.alertId)

    this.alarmService.removeAlarmReasonsFromStorage(alert.alertId)

    this.api.setReasonsToAlert(requestObject).subscribe(
      noop,
      noop,
      () => {
        this.crService.takeAction(alert)
      }
    )
  }

  /**
   * When the alert's recipients are known but none of them are available, the
   * time the alert server last saw any of them - the operator's cue for whether
   * this is a short absence or an empty shift. Undefined when someone is
   * available, when the route has no recipients, or when no recipient has ever
   * been seen, none of which have a time to show.
   *
   * @param {CrAlert} alert - The alert shown on the card.
   */
  lastAppUserSeen(alert: CrAlert): string | undefined {
    if (this.crService.appUsersAvailable(alert)) return undefined;
    // with members of the route we cannot see, the most recent time we have is
    // not the most recent time there is
    if (alert.redirectRoute === CrRedirectRoute.Unknown) return undefined;

    // recipients come sorted most recently seen first
    return this.crService.appRecipients(alert)[0]?.lastSeen;
  }

  /**
   * How many of the alert's recipients would receive it right now. Undefined
   * when nobody is available, and also when the recipients are not known -
   * an alert server that does not report them, or a team whose state may be
   * stale - since the send is allowed on those but there is no number behind
   * it to show.
   *
   * @param {CrAlert} alert - The alert shown on the card.
   */
  availableAppRecipientCount(alert: CrAlert): number | undefined {
    // a fail-open "available" is a guess, and a guess must not be shown as a
    // number: without complete presence the cached online states can be old
    if (!this.crService.appPresenceKnown(alert)) return undefined;
    if (!this.crService.appUsersAvailable(alert)) return undefined;

    // recipients the session's team does not know are left out rather than
    // suppressing the whole count: they can only add to it, never subtract, so
    // this is the number of app users the alarm is known to reach. Alarm routes
    // routinely name people a session cannot see - other user groups, staff who
    // have never been online - and refusing to count then says nothing at all
    // about a shift that is in fact staffed.
    const online = this.crService.appRecipients(alert)
      .filter((recipient) => recipient.known && recipient.isOnline).length;
    return online > 0 ? online : undefined;
  }

  /**
   * True when the alarm route is known to have no recipients at all, which is a
   * configuration fault rather than a staffing one. An alert that does not
   * report its recipients is not this: they are unknown, not absent.
   *
   * @param {CrAlert} alert - The alert shown on the card.
   */
  appRecipientsMissing(alert: CrAlert): boolean {
    return !this.crService.appUsersAvailable(alert) &&
      alert.redirectRoute === CrRedirectRoute.NoTargets;
  }

  /**
   * The tail of the card's availability notices: the alert server reports app
   * user presence, but none of the other notices applied, so we could not tell
   * whether a recipient would get this alarm. Recipients the alert does not
   * name, or that the session's team does not know, both end here.
   *
   * False against an alert server that does not report presence at all, where
   * the card stays silent as it did before any of this existed.
   */
  get appAvailabilityUnknown(): boolean {
    return this.crSync.appUserPresenceComplete;
  }

  /**
   * True when nobody would receive the alert and there is nothing more specific
   * to say about it - no recipient with a time to show, and no empty route to
   * blame. The case behind it is an alert that does not name its recipients,
   * where all we know is that no app user of the customer is online.
   *
   * @param {CrAlert} alert - The alert shown on the card.
   */
  noAppUsersAvailable(alert: CrAlert): boolean {
    return !this.crService.appUsersAvailable(alert);
  }

  /**
   * Forwards an alert to the app, asking the operator to confirm first when no
   * recipient is available. Sending anyway is allowed - the operator may know
   * something the presence data does not - but the alarm would come straight
   * back to the control room with degraded information, so it is not something
   * to do without seeing it.
   *
   * @param {CrAlert} alert - The alert the operator wants to send to the app.
   */
  confirmSendToApp(alert: CrAlert) {
    if (this.crService.appUsersAvailable(alert)) {
      this.sendToApp(alert);
      return;
    }

    const dialogRef = this.dialog.open(SendToAppConfirmDialogComponent, {
      width: '600px',
      data: {
        recipients: this.crService.appRecipients(alert),
        routeIsEmpty: alert.redirectRoute === CrRedirectRoute.NoTargets
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.sendToApp(alert)
      }
    })
  }

  /**
   * Nightowl camera visits are alerts and are forwarded to the app as such,
   * so they go through the same confirmation. Other visits generate a new
   * alert, which is routed by the alarm route instead of being forwarded here.
   *
   * @param {any} visit - The visit (or Nightowl visit alert) shown on the card.
   */
  sendVisitToApp(visit: any) {
    if (this.crService.alertIsNightowlVisit(visit)) {
      this.confirmSendToApp(visit);
    } else {
      this.generateAlertFromVisit(visit);
    }
  }

  cancelReservation(alert: CrAlert) {
    // clean alarm reasons which were attached to the alert temporarily
    this.alarmService.deleteAlarmReasonsFromAll(alert.alertId)
    this.alarmService.removeAlarmReasonsFromStorage(alert.alertId)
    this.alarmService.removeFromEnabledAlarms(alert.alertId)
    this.iFrameClicked = null;

    this.crService.cancelReservation(alert)
  }

  snoozeVisit(visit: any) {
    if (visit.cameras?.length > 0) {
      this.crService.setVideoPlayback(visit, null, false, true); // set to complete
      this.crService.removeFromVideoPlayback(visit.id);
    }

    this.crService.snoozeVisit(visit);
  }

  hideForm() {
    this.showNoteForm = false
  }

  callPatient(baseMISDN: string, visitId: string) {
    if (baseMISDN && visitId) {
      // Log the call start to Lyra
      this.crSync.setPhoneCallStarted(baseMISDN, visitId);
      window.location.href = "tel:" + baseMISDN;
    }
  }

  activateIframeClick(dataId: string): void {
    this.iFrameClicked = dataId;
  }

  previousTime(previous: Date): string {
    const locale = this.language === "en" ? "en-GB" : this.language ? this.language : "en-GB";
    return previous ?
      this.translate.instant("PREVIOUS_VISITED") +
      ' ' +
      new Date(previous).toLocaleDateString(locale).replace(/[.-]/g, '/') +
      ' ' +
      new Date(previous).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
      :
      this.translate.instant("PREVIOUS_VISITED_MISSING");
  }

  getAddressString(data: any): string {
    return `${data.address}${data.postCode ? ', ' + data.postCode : ''}${data.postOffice ? ' ' + data.postOffice : ''}`;
  }

  completeStartedVisit(visit: any) {
    if (visit.cameras?.length > 0) {
      this.crService.setVideoPlayback(visit, null, false, true); // set to complete
      this.crService.removeFromVideoPlayback(visit.id);
    }

    this.crService.completeStartedVisit(visit);
  }

  generateAlertFromVisit(visit: any) {
    if (visit.cameras?.length > 0) {
      this.crService.setVideoPlayback(visit, null, false, true); // set to complete
      this.crService.removeFromVideoPlayback(visit.id);
    }

    this.crService.generateAlertFromVisit(visit);
  }

  /** This is only for normal camera visits, not for Nightowl camera visit. */
  isLoaded(ev: any) {
    const visit = this.visits.find((v: any) => v.id === ev.data.visitId);
    const data = ev.data;
    if (!visit) {
      return;
    }
    if (data.status === 'loaded') {
      visit.cameras?.forEach((camera: any) => {
        this.crService.setVideoPlayback(visit, camera.deviceId, true, false);  // set to start
      });
    }
  }

}
